<script setup lang="ts">
const content = {
  title: 'Zo vind je de juiste makelaar',
  description: 'Een makelaar kies je niet elke dag. Daarom vergelijken we makelaars op wat ze echt hebben gedaan, niet op wat ze beloven.',
  steps: [
    {
      icon: 'i-lucide-clipboard-list',
      title: '1. Vertel wat je zoekt',
      text: 'Wil je je huis verkopen, een woning kopen of laten taxeren? Vul je postcode en een paar voorkeuren in. Dat duurt een paar minuten.',
    },
    {
      icon: 'i-lucide-bar-chart-3',
      title: '2. Wij vergelijken',
      text: 'We leggen jouw situatie naast de verkoopcijfers van makelaars bij jou in de buurt. We bellen je kort om de match compleet te maken.',
    },
    {
      icon: 'i-lucide-handshake',
      title: '3. Jij kiest',
      text: 'Je krijgt de best passende makelaars en zij nemen contact met je op. Jij kiest met wie je verder gaat. Gratis en zonder verplichtingen.',
    },
  ],
  criteriaTitle: 'Waarop we makelaars vergelijken',
  criteria: [
    { title: 'Verkoopsnelheid', text: 'Hoeveel dagen een woning van deze makelaar gemiddeld te koop staat.' },
    { title: 'Verkoopprijs', text: 'Hoe de verkoopprijs zich verhoudt tot de vraagprijs en de WOZ-waarde.' },
    { title: 'Ervaring in jouw buurt', text: 'Hoeveel woningen de makelaar het afgelopen jaar in jouw plaats verkocht.' },
    { title: 'Reviews', text: 'Wat klanten op Google over de makelaar zeggen.' },
  ],
  linksTitle: 'Meer weten?',
  links: [
    { label: 'Verkoopmakelaar vergelijken', to: '/verkoopmakelaar/' },
    { label: 'Aankoopmakelaar vergelijken', to: '/aankoopmakelaar/' },
    { label: 'Taxateur vinden', to: '/taxateur/' },
    { label: 'Grootste makelaars van Nederland', to: '/makelaar/' },
    { label: 'Makelaars per provincie', to: '/makelaars/' },
  ],
}
</script>

<template>
  <section class="py-16 bg-muted">
    <UContainer>
      <div class="text-center mb-10 max-w-2xl mx-auto">
        <h2 class="text-3xl sm:text-4xl font-bold tracking-[-0.015em] text-highlighted text-balance mb-2">
          {{ content.title }}
        </h2>
        <p class="text-muted text-lg text-pretty">
          {{ content.description }}
        </p>
      </div>

      <ol class="grid gap-6 md:grid-cols-3 mb-12">
        <li
          v-for="step in content.steps"
          :key="step.title"
          class="rounded-lg bg-default p-6 ring ring-default"
        >
          <UIcon
            :name="step.icon"
            class="size-6 text-primary mb-3"
          />
          <h3 class="font-semibold text-highlighted mb-1">
            {{ step.title }}
          </h3>
          <p class="text-muted text-pretty">
            {{ step.text }}
          </p>
        </li>
      </ol>

      <div class="grid gap-10 lg:grid-cols-3">
        <div class="lg:col-span-2">
          <h3 class="text-xl font-semibold text-highlighted mb-4">
            {{ content.criteriaTitle }}
          </h3>
          <dl class="grid gap-4 sm:grid-cols-2">
            <div
              v-for="item in content.criteria"
              :key="item.title"
            >
              <dt class="font-medium text-highlighted">
                {{ item.title }}
              </dt>
              <dd class="text-muted">
                {{ item.text }}
              </dd>
            </div>
          </dl>
        </div>
        <nav :aria-label="content.linksTitle">
          <h3 class="text-xl font-semibold text-highlighted mb-4">
            {{ content.linksTitle }}
          </h3>
          <ul class="space-y-2">
            <li
              v-for="link in content.links"
              :key="link.to"
            >
              <ULink
                :to="link.to"
                class="inline-flex items-center gap-1 text-primary hover:underline"
              >
                <UIcon
                  name="i-lucide-arrow-right"
                  class="size-4"
                />
                {{ link.label }}
              </ULink>
            </li>
          </ul>
        </nav>
      </div>
    </UContainer>
  </section>
</template>
